import React, { useEffect, useMemo, useRef } from "react";
import { parseLrc, findActiveIndex } from "@/lib/lrc";

/**
 * Cinematic synced lyric view.
 * Active line scales up + glows; surrounding lines fade with blur falloff based on distance.
 */
export default function LyricsView({ lyrics, currentTime, onSeek }) {
  const { lines, synced } = useMemo(() => parseLrc(lyrics || ""), [lyrics]);
  const containerRef = useRef(null);
  const activeIndex = useMemo(
    () => (synced ? findActiveIndex(lines, currentTime || 0) : -1),
    [synced, lines, currentTime]
  );

  // Auto-scroll the active line into the centre.
  useEffect(() => {
    if (!synced || activeIndex < 0) return;
    const container = containerRef.current;
    if (!container) return;
    const el = container.querySelector(`[data-lyric-index="${activeIndex}"]`);
    if (!el) return;
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const offset =
      elRect.top - containerRect.top - containerRect.height / 2 + elRect.height / 2;
    container.scrollBy({ top: offset, behavior: "smooth" });
  }, [activeIndex, synced]);

  if (!lyrics || lines.length === 0) {
    return (
      <div
        className="h-full flex items-center justify-center text-center px-8"
        data-testid="lyrics-empty"
      >
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/30 mb-3">
            // no lyrics attached
          </div>
          <div className="font-display font-black text-3xl tracking-tighter text-white/30">
            Just the music for now.
          </div>
          <div className="text-white/40 text-sm mt-3 max-w-sm mx-auto">
            Paste lyrics, upload a .lrc, or hit auto-fetch from the panel on the left.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="h-full overflow-y-auto lyrics-mask no-scrollbar py-[40vh]"
      data-testid="lyrics-view"
    >
      <div className="space-y-6 px-4 md:px-8">
        {lines.map((l, i) => {
          const distance = synced && activeIndex >= 0 ? Math.abs(i - activeIndex) : 999;
          const isActive = synced && i === activeIndex;
          const isPast = synced && i < activeIndex;

          // Distance-based opacity, blur, and scale for cinematic falloff.
          let opacity = 1;
          let blur = 0;
          let scaleStyle = {};
          if (synced) {
            if (isActive) {
              opacity = 1;
              blur = 0;
            } else {
              opacity = Math.max(0.18, 0.85 - distance * 0.18);
              blur = Math.min(2.5, Math.max(0, distance - 1) * 0.7);
            }
          } else {
            opacity = 0.55;
          }

          return (
            <button
              key={i}
              type="button"
              data-lyric-index={i}
              onClick={() => {
                if (synced && l.time !== null && onSeek) onSeek(l.time);
              }}
              className={`block w-full text-left transition-all duration-500 ease-out ${
                isActive
                  ? "active-lyric-glow text-white font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-tight"
                  : "font-display tracking-tight leading-snug text-xl sm:text-2xl hover:!opacity-100 hover:!blur-none " +
                    (isPast ? "text-white/40" : "text-white/55")
              }`}
              style={{
                opacity,
                filter: blur ? `blur(${blur}px)` : "none",
                ...scaleStyle,
              }}
              data-testid={isActive ? "lyrics-active-line" : undefined}
            >
              {l.text || <span className="text-white/15">♪</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}
